import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { KPI } from "@/modules/hub/features/MemberDashboard/api/fetchMemberDashboard/types";
import {
  KPI_CONFIG,
  SECTIONS,
  SectionCode,
} from "@/modules/hub/features/MemberDashboard/config/kpiConfig";
import {
  formatDelta,
  toDashboardKPI,
} from "@/modules/hub/features/MemberDashboard/utils/toDashboardKPI";
import {
  DashboardKPI,
  KPIUnit,
} from "@/modules/hub/features/MemberDashboard/types";
import { FetchEntitiesFn } from "@/components/SingleEntityPickerModal/SingleEntityPickerModal";
import { fetchKPIOverview } from "../api/fetchKPIOverview/fetchKPIOverviewApi";
import { fetchKPIOverviewCoaches } from "../api/fetchKPIOverviewCoaches/fetchKPIOverviewCoachesApi";
import { KPIOverviewCoach } from "../api/fetchKPIOverviewCoaches/types";
import {
  CompanyKPIOverview,
  KPIOverviewSegmentCounts,
} from "../api/fetchKPIOverview/types";
import { KPI_SHORT_LABELS } from "../config/kpiShortLabels";
import {
  ChangeTone,
  KPIOverviewPanel,
  KPIOverviewRow,
  KPIOverviewTileModel,
} from "../types";
import {
  resolveCompanyStatus,
  resolveKPIStatus,
} from "../utils/kpiOverviewStatus";
import {
  DEFAULT_KPI_OVERVIEW_VIEW,
  parseKPIOverviewView,
} from "../utils/kpiOverviewView";
import {
  DEFAULT_KPI_OVERVIEW_SEGMENT,
  EMPTY_KPI_OVERVIEW_SEGMENT_COUNTS,
  KPI_OVERVIEW_SEGMENTS,
  KPIOverviewSegment,
  parseKPIOverviewSegment,
} from "../utils/kpiOverviewSegment";
import {
  DEFAULT_KPI_OVERVIEW_SORT,
  parseKPIOverviewSort,
} from "../utils/kpiOverviewSort";
import { buildSparkline } from "../utils/sparkline";

export const KPI_OVERVIEW_PAGE_SIZE = 10;

const VIEW_PARAM = "view";

const SEGMENT_PARAM = "segment";

const SORT_PARAM = "sort";

/** The worked example is the only consumer of the period length, and tiles don't show it. */
const UNUSED_PERIOD_DAYS = 0;

const SECTION_CODES = Object.keys(SECTIONS) as SectionCode[];

const codesInSection = (section: SectionCode): string[] =>
  Object.entries(KPI_CONFIG)
    .filter(([, config]) => config.section === section)
    .map(([code]) => code);

/** "48d" for a days KPI; every other unit as the dashboard writes it. */
const describeValue = (kpi: DashboardKPI): string | null => {
  if (kpi.value === null || kpi.unit !== "days") {
    return kpi.valueLabel;
  }

  return `${Math.round(kpi.value)}d`;
};

/**
 * Percent against the year-ago figure for money ("5.4%"), "2d" for days, and
 * the dashboard's own wording for the rest ("0.9 pts", "0.11"). The year-ago
 * figure is the latest value minus the change. Money falls back to the amount
 * when that figure is zero, since there's no percent to give.
 */
const formatChangeMagnitude = (
  value: number,
  delta: number,
  unit: KPIUnit,
): string => {
  if (unit === "days") {
    return `${Math.round(Math.abs(delta))}d`;
  }

  const yearAgoValue = value - delta;

  if (unit === "currency" && yearAgoValue !== 0) {
    return `${Math.abs((delta / Math.abs(yearAgoValue)) * 100).toFixed(1)}%`;
  }

  return formatDelta(delta, unit);
};

const describeChange = (kpi: KPI): string | null => {
  const delta = kpi.yearOverYearDelta;

  if (
    delta === null ||
    kpi.comparedToYearMonth === null ||
    kpi.value === null
  ) {
    return null;
  }

  const magnitude = formatChangeMagnitude(
    kpi.value,
    delta,
    KPI_CONFIG[kpi.code].unit,
  );

  if (delta > 0) {
    return `↑ ${magnitude}`;
  }

  return delta < 0 ? `↓ ${magnitude}` : magnitude;
};

/** Good or bad for this KPI's direction; no change, or none to show, is neutral. */
const describeChangeTone = (
  kpi: KPI,
  dashboardKPI: DashboardKPI,
): ChangeTone => {
  if (!kpi.yearOverYearDelta || kpi.comparedToYearMonth === null) {
    return "neutral";
  }

  return dashboardKPI.deltaIsGood ? "good" : "bad";
};

const createTile = (kpi: KPI): KPIOverviewTileModel => {
  const dashboardKPI = toDashboardKPI(
    kpi,
    KPI_CONFIG[kpi.code],
    UNUSED_PERIOD_DAYS,
  );

  return {
    code: kpi.code,
    name: dashboardKPI.name,
    shortLabel: KPI_SHORT_LABELS[kpi.code],
    valueLabel: describeValue(dashboardKPI),
    changeLabel: describeChange(kpi),
    changeTone: describeChangeTone(kpi, dashboardKPI),
    status: resolveKPIStatus(dashboardKPI),
    sparkline: buildSparkline(dashboardKPI.chart.map((point) => point.value)),
  };
};

/** A KPI the company has no figures for, e.g. no ServiceTitan connection. */
const createPlaceholderTile = (code: string): KPIOverviewTileModel => ({
  code,
  name: KPI_CONFIG[code].name,
  shortLabel: KPI_SHORT_LABELS[code],
  valueLabel: null,
  changeLabel: null,
  changeTone: "neutral",
  status: "none",
  sparkline: buildSparkline([]),
});

const createRows = (kpis: KPI[]): KPIOverviewRow[] => {
  const kpisByCode = new Map(kpis.map((kpi) => [kpi.code, kpi]));

  return SECTION_CODES.map((section) => ({
    code: section,
    title: SECTIONS[section].title,
    source: SECTIONS[section].source,
    tiles: codesInSection(section).map((code) => {
      const kpi = kpisByCode.get(code);

      return kpi ? createTile(kpi) : createPlaceholderTile(code);
    }),
  }));
};

const describeLocation = (company: CompanyKPIOverview): string | null => {
  const parts = [company.city, company.state].filter(
    (part): part is string => !!part,
  );

  return parts.length > 0 ? parts.join(", ") : null;
};

const createPanel = (company: CompanyKPIOverview): KPIOverviewPanel => {
  const rows = createRows(company.kpis);

  return {
    uuid: company.uuid,
    companyName: company.companyName,
    location: describeLocation(company),
    trades: company.trades,
    coachName: company.coachName,
    overallStatus: resolveCompanyStatus(
      rows.flatMap((row) => row.tiles.map((tile) => tile.status)),
    ),
    rows,
  };
};

const describeSegmentTitle = (segment: KPIOverviewSegment): string =>
  KPI_OVERVIEW_SEGMENTS.find((option) => option.value === segment)?.label ??
  KPI_OVERVIEW_SEGMENTS[0].label;

/** The last completed month is the one the tiles report: "September 2026 close". */
export const describePeriod = (today: Date): string => {
  const lastCompletedMonth = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    1,
  );
  const monthLabel = lastCompletedMonth.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });

  return `${monthLabel} close · trailing 12 months`;
};

export function useKPIOverviewPage() {
  const [companies, setCompanies] = useState<CompanyKPIOverview[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [coach, setCoach] = useState<KPIOverviewCoach | null>(null);
  const [segmentCounts, setSegmentCounts] = useState<KPIOverviewSegmentCounts>(
    EMPTY_KPI_OVERVIEW_SEGMENT_COUNTS,
  );
  const [visibleSections, setVisibleSections] =
    useState<SectionCode[]>(SECTION_CODES);
  const [searchParams, setSearchParams] = useSearchParams();

  const view = parseKPIOverviewView(searchParams.get(VIEW_PARAM));
  const segment = parseKPIOverviewSegment(searchParams.get(SEGMENT_PARAM));
  const sort = parseKPIOverviewSort(searchParams.get(SORT_PARAM));
  const coachEmployeeUuid = coach?.id;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    fetchKPIOverview(
      {
        page,
        pageSize: KPI_OVERVIEW_PAGE_SIZE,
        searchTerm: searchTerm || undefined,
        coachEmployeeUuid,
        segment: segment === DEFAULT_KPI_OVERVIEW_SEGMENT ? undefined : segment,
        sort,
      },
      controller.signal,
    )
      .then((result) => {
        setCompanies(result.companyKPIOverviews);
        setSegmentCounts(result.segmentCounts);
        setTotalCount(result.totalCount);
      })
      .catch(() => {})
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [page, searchTerm, coachEmployeeUuid, segment, sort]);

  /** Hiding a section hides its rows; the overall status still counts all fifteen KPIs. */
  const panels = useMemo(
    () =>
      companies.map(createPanel).map((panel) => ({
        ...panel,
        rows: panel.rows.filter((row) => visibleSections.includes(row.code)),
      })),
    [companies, visibleSections],
  );

  /** The last section shown can't be hidden, so the page is never empty. */
  const handleVisibleSectionsChange = useCallback((sections: string[]) => {
    if (sections.length === 0) {
      return;
    }

    setVisibleSections(
      SECTION_CODES.filter((section) => sections.includes(section)),
    );
  }, []);

  const pageCount = Math.max(1, Math.ceil(totalCount / KPI_OVERVIEW_PAGE_SIZE));

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPage(1);
  }, []);

  const handleCoachChange = useCallback(
    (nextCoach: KPIOverviewCoach | null) => {
      setCoach(nextCoach);
      setPage(1);
    },
    [],
  );

  const fetchCoaches: FetchEntitiesFn<KPIOverviewCoach> = useCallback(
    ({ searchTerm: coachSearchTerm, page: coachPage, pageSize }) =>
      fetchKPIOverviewCoaches({
        searchTerm: coachSearchTerm || undefined,
        page: coachPage,
        pageSize,
      }),
    [],
  );

  /** Cards is the default, so it leaves no parameter behind. */
  const handleViewChange = useCallback(
    (nextView: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          const parsedView = parseKPIOverviewView(nextView);

          if (parsedView === DEFAULT_KPI_OVERVIEW_VIEW) {
            next.delete(VIEW_PARAM);
          } else {
            next.set(VIEW_PARAM, parsedView);
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /** All members is the default, so it leaves no parameter behind. */
  const handleSegmentChange = useCallback(
    (nextSegment: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          const parsedSegment = parseKPIOverviewSegment(nextSegment);

          if (parsedSegment === DEFAULT_KPI_OVERVIEW_SEGMENT) {
            next.delete(SEGMENT_PARAM);
          } else {
            next.set(SEGMENT_PARAM, parsedSegment);
          }

          return next;
        },
        { replace: true },
      );
      setPage(1);
    },
    [setSearchParams],
  );

  /** Health is the default, so it leaves no parameter behind. */
  const handleSortChange = useCallback(
    (nextSort: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          const parsedSort = parseKPIOverviewSort(nextSort);

          if (parsedSort === DEFAULT_KPI_OVERVIEW_SORT) {
            next.delete(SORT_PARAM);
          } else {
            next.set(SORT_PARAM, parsedSort);
          }

          return next;
        },
        { replace: true },
      );
      setPage(1);
    },
    [setSearchParams],
  );

  const goToPreviousPage = useCallback(
    () => setPage((current) => Math.max(1, current - 1)),
    [],
  );

  const goToNextPage = useCallback(
    () => setPage((current) => Math.min(pageCount, current + 1)),
    [pageCount],
  );

  return {
    panels,
    totalCount,
    loading,
    page,
    pageCount,
    hasPreviousPage: page > 1,
    hasNextPage: page < pageCount,
    searchTerm,
    view,
    segment,
    segmentTitle: describeSegmentTitle(segment),
    segmentCounts,
    sort,
    periodLabel: describePeriod(new Date()),
    coach,
    fetchCoaches,
    visibleSections,
    handleVisibleSectionsChange,
    handleSearchChange,
    handleCoachChange,
    handleViewChange,
    handleSegmentChange,
    handleSortChange,
    goToPreviousPage,
    goToNextPage,
  };
}
